import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LayoutGrid, Inbox, Settings as SettingsIcon, LogOut, Plus, Trash2,
  Pencil, ExternalLink, Building2, Check, X, Phone, Mail, HardHat, Wrench, Star, Briefcase, FileText,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MultiImageUpload, SingleImageUpload, FileUpload } from "@/components/admin/AdminUploader";
import { SERVICE_ICON_OPTIONS } from "@/data/icons";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const TABS = [
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "careers", label: "Careers", icon: Briefcase },
  { key: "projects", label: "Portfolio", icon: LayoutGrid },
  { key: "progress", label: "Work in Progress", icon: HardHat },
  { key: "services", label: "Services", icon: Wrench },
  { key: "testimonials", label: "Testimonials", icon: Star },
  { key: "settings", label: "Site Settings", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("enquiries");
  const { logout, user } = useAuth();

  return (
    <div className="min-h-screen bg-cream flex" data-testid="admin-dashboard">
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-navy-deep text-white p-6 sticky top-0 h-screen">
        <div className="flex items-center gap-3 mb-10">
          <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-white/95 p-1">
            <img src="/uyi-logo.png" alt="UYI logo" className="h-full w-full object-contain" />
          </span>
          <span className="font-display text-lg">Admin</span>
        </div>
        <nav className="flex-1 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex w-full items-center gap-3 rounded-sm px-4 py-3 text-sm transition-colors duration-200 ${
                  tab === t.key ? "bg-gold text-navy" : "text-white/60 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </nav>
        <Link to="/" className="flex items-center gap-2 text-white/40 text-sm hover:text-white mb-3">
          <ExternalLink className="h-4 w-4" /> View site
        </Link>
        <button
          data-testid="admin-logout"
          onClick={logout}
          className="flex items-center gap-2 text-white/60 text-sm hover:text-white"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-navy/10 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-xl text-navy capitalize">{TABS.find((t) => t.key === tab)?.label}</h1>
            <p className="text-xs text-navy/40">Signed in as {user?.email}</p>
          </div>
          <div className="md:hidden flex gap-2">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-sm px-3 py-1.5 text-xs ${tab === t.key ? "bg-navy text-white" : "bg-navy/5 text-navy"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {tab === "enquiries" && <Enquiries />}
          {tab === "careers" && <Careers />}
          {tab === "projects" && <Projects />}
          {tab === "progress" && <Progress />}
          {tab === "services" && <Services />}
          {tab === "testimonials" && <Testimonials />}
          {tab === "settings" && <SiteSettings />}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------- Enquiries */
function Enquiries() {
  const qc = useQueryClient();
  const { data: enquiries = [], isLoading } = useQuery({
    queryKey: ["enquiries"],
    queryFn: async () => (await api.get("/enquiries")).data,
  });

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/enquiries/${id}`);
    qc.invalidateQueries({ queryKey: ["enquiries"] });
    toast.success("Enquiry deleted");
  };

  const toggleStatus = async (e) => {
    const next = e.status === "new" ? "handled" : "new";
    await api.patch(`/enquiries/${e.id}?status=${next}`);
    qc.invalidateQueries({ queryKey: ["enquiries"] });
  };

  if (isLoading) return <p className="text-navy/50">Loading…</p>;

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-3 mb-8">
        <StatCard label="Total enquiries" value={enquiries.length} />
        <StatCard label="New" value={enquiries.filter((e) => e.status === "new").length} />
        <StatCard label="Handled" value={enquiries.filter((e) => e.status === "handled").length} />
      </div>

      {enquiries.length === 0 ? (
        <EmptyState title="No enquiries yet" desc="Submissions from the contact form will appear here." />
      ) : (
        <div className="overflow-hidden rounded-sm border border-navy/10 bg-white">
          <table className="w-full text-sm" data-testid="enquiries-table">
            <thead className="bg-navy text-white/80 text-left text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Contact</th>
                <th className="px-5 py-3">Service</th>
                <th className="px-5 py-3">Message</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.map((e) => (
                <tr key={e.id} className="border-t border-navy/5 align-top" data-testid="enquiry-row">
                  <td className="px-5 py-4 font-medium text-navy">{e.name}</td>
                  <td className="px-5 py-4 text-navy/70">
                    {e.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" />{e.phone}</div>}
                    {e.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" />{e.email}</div>}
                  </td>
                  <td className="px-5 py-4 text-navy/70">{e.service || "—"}</td>
                  <td className="px-5 py-4 text-navy/60 max-w-xs">{e.message || "—"}</td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => toggleStatus(e)}
                      className={`rounded-full px-3 py-1 text-xs ${e.status === "new" ? "bg-gold/20 text-gold" : "bg-navy/10 text-navy/60"}`}
                    >
                      {e.status}
                    </button>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => remove(e.id)} data-testid="enquiry-delete" className="text-navy/40 hover:text-red-500 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------- Projects */
const EMPTY_PROJECT = { title: "", category: "Construction", location: "", year: "", description: "", image: "", images: [], featured: false, status: "completed", order: 0 };

function Projects() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PROJECT);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects", "admin"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  const openNew = () => { setEditing(null); setForm(EMPTY_PROJECT); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY_PROJECT, ...p }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title || !form.image) { toast.error("Title and cover image URL are required."); return; }
    const payload = {
      ...form,
      order: Number(form.order) || 0,
      images: (form.images || []).map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/projects/${editing.id}`, payload);
      else await api.post("/projects", payload);
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success(editing ? "Project updated" : "Project added");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    qc.invalidateQueries({ queryKey: ["projects"] });
    toast.success("Project deleted");
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-navy/50 text-sm">{projects.length} projects</p>
        <button onClick={openNew} data-testid="project-add" className="inline-flex items-center gap-2 rounded-sm bg-navy px-5 py-2.5 text-white text-sm hover:bg-gold hover:text-navy transition-colors">
          <Plus className="h-4 w-4" /> Add project
        </button>
      </div>

      {isLoading ? <p className="text-navy/50">Loading…</p> : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
          {projects.map((p) => (
            <div key={p.id} className="group overflow-hidden rounded-sm border border-navy/10 bg-white" data-testid="project-card">
              <div className="relative h-44 overflow-hidden">
                <img src={p.image} alt={p.title} className="h-full w-full object-cover" />
                {p.featured && <span className="absolute top-3 left-3 rounded-full bg-gold px-3 py-0.5 text-xs text-navy">Featured</span>}
              </div>
              <div className="p-5">
                <span className="text-xs uppercase tracking-wider text-gold">{p.category}</span>
                <h3 className="mt-1 font-display text-lg text-navy">{p.title}</h3>
                <p className="text-xs text-navy/40 mt-1">{p.location} {p.year && `· ${p.year}`}</p>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => openEdit(p)} data-testid="project-edit" className="flex-1 inline-flex items-center justify-center gap-1 rounded-sm border border-navy/15 py-2 text-xs text-navy hover:bg-navy hover:text-white transition-colors">
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  <button onClick={() => remove(p.id)} data-testid="project-delete" className="rounded-sm border border-navy/15 px-3 py-2 text-navy/50 hover:text-red-500 hover:border-red-300 transition-colors">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-navy">{editing ? "Edit project" : "New project"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <AdminField label="Title"><Input value={form.title} onChange={set("title")} data-testid="project-title" className="rounded-sm" /></AdminField>
            <div className="grid grid-cols-2 gap-4">
              <AdminField label="Category">
                <select value={form.category} onChange={set("category")} data-testid="project-category" className="h-10 w-full rounded-sm border border-input px-3 text-sm">
                  <option>Construction</option><option>Interior</option><option>Furniture</option>
                </select>
              </AdminField>
              <AdminField label="Year"><Input value={form.year} onChange={set("year")} className="rounded-sm" /></AdminField>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <AdminField label="Location"><Input value={form.location} onChange={set("location")} className="rounded-sm" /></AdminField>
              <AdminField label="Order"><Input type="number" value={form.order} onChange={set("order")} className="rounded-sm" /></AdminField>
            </div>
            <AdminField label="Status">
              <select value={form.status} onChange={set("status")} data-testid="project-status" className="h-10 w-full rounded-sm border border-input px-3 text-sm">
                <option value="completed">Completed</option>
                <option value="ongoing">Ongoing</option>
              </select>
            </AdminField>
            <AdminField label="Cover image">
              <SingleImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="project-cover-upload" />
            </AdminField>
            {form.image && <img src={form.image} alt="preview" className="h-32 w-full object-cover rounded-sm border border-navy/10" />}
            <AdminField label="Gallery photos (before / after & multiple stages)">
              <MultiImageUpload images={form.images || []} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} testid="project-gallery" />
            </AdminField>
            <AdminField label="Description"><Textarea value={form.description} onChange={set("description")} rows={3} className="rounded-sm" /></AdminField>
            <label className="flex items-center gap-2 text-sm text-navy">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} data-testid="project-featured" />
              Show as featured on homepage
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-navy/15 px-5 py-2.5 text-sm text-navy">Cancel</button>
            <button onClick={save} data-testid="project-save" className="rounded-sm bg-navy px-6 py-2.5 text-sm text-white hover:bg-gold hover:text-navy transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------------------------- Careers */
function Careers() {
  const qc = useQueryClient();
  const { data: apps = [], isLoading } = useQuery({
    queryKey: ["careers"],
    queryFn: async () => (await api.get("/careers")).data,
  });
  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await api.delete(`/careers/${id}`);
    qc.invalidateQueries({ queryKey: ["careers"] });
    toast.success("Deleted");
  };
  if (isLoading) return <p className="text-navy/50">Loading…</p>;
  return (
    <div>
      <StatCard label="Total applications" value={apps.length} />
      {apps.length === 0 ? (
        <div className="mt-6"><EmptyState title="No applications yet" desc="Career applications from the website will appear here." /></div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-sm border border-navy/10 bg-white">
          <table className="w-full text-sm" data-testid="careers-table">
            <thead className="bg-navy text-white/80 text-left text-xs uppercase tracking-wider">
              <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Contact</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Message</th><th className="px-5 py-3">CV</th><th className="px-5 py-3 text-right">Action</th></tr>
            </thead>
            <tbody>
              {apps.map((e) => (
                <tr key={e.id} className="border-t border-navy/5 align-top" data-testid="career-row">
                  <td className="px-5 py-4 font-medium text-navy">{e.name}</td>
                  <td className="px-5 py-4 text-navy/70">
                    {e.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" />{e.phone}</div>}
                    {e.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" />{e.email}</div>}
                  </td>
                  <td className="px-5 py-4 text-navy/70">{e.role || "—"}</td>
                  <td className="px-5 py-4 text-navy/60 max-w-xs">{e.message || "—"}</td>
                  <td className="px-5 py-4">
                    {e.cv_url ? (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${e.cv_url}`} target="_blank" rel="noreferrer" data-testid="career-cv-link" className="inline-flex items-center gap-1 text-gold hover:underline"><FileText className="h-4 w-4" /> View</a>
                    ) : <span className="text-navy/30">—</span>}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => remove(e.id)} data-testid="career-delete" className="text-navy/40 hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------- Work in Progress */
const EMPTY_PROGRESS = { title: "", location: "", stage: "", description: "", images: [], order: 0 };

function Progress() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PROGRESS);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["progress", "admin"],
    queryFn: async () => (await api.get("/progress")).data,
  });

  const openNew = () => { setEditing(null); setForm(EMPTY_PROGRESS); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY_PROGRESS, ...p }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title) { toast.error("Title is required."); return; }
    const payload = { ...form, order: Number(form.order) || 0, images: (form.images || []).filter(Boolean) };
    try {
      if (editing) await api.put(`/progress/${editing.id}`, payload);
      else await api.post("/progress", payload);
      qc.invalidateQueries({ queryKey: ["progress"] });
      toast.success(editing ? "Updated" : "Added");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/progress/${id}`);
    qc.invalidateQueries({ queryKey: ["progress"] });
    toast.success("Deleted");
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-navy/50 text-sm">{items.length} ongoing projects — shown in the homepage "Work in Progress" section</p>
        <button onClick={openNew} data-testid="progress-add" className="inline-flex items-center gap-2 rounded-sm bg-navy px-5 py-2.5 text-white text-sm hover:bg-gold hover:text-navy transition-colors">
          <Plus className="h-4 w-4" /> Add project
        </button>
      </div>

      {isLoading ? <p className="text-navy/50">Loading…</p> : items.length === 0 ? (
        <EmptyState title="No ongoing projects yet" desc="Add a live site with stage-by-stage photos to show it on the homepage." />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="progress-grid">
          {items.map((p) => (
            <div key={p.id} className="overflow-hidden rounded-sm border border-navy/10 bg-white" data-testid="progress-card">
              <div className="relative h-40 overflow-hidden bg-navy/5">
                {p.images?.[0] && <img src={p.images[0]} alt={p.title} className="h-full w-full object-cover" />}
                {p.stage && <span className="absolute top-3 left-3 rounded-full bg-gold px-3 py-0.5 text-xs text-navy">{p.stage}</span>}
                {p.images?.length > 1 && <span className="absolute bottom-3 right-3 rounded-full bg-navy-deep/70 px-2.5 py-0.5 text-xs text-white">{p.images.length} photos</span>}
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg text-navy">{p.title}</h3>
                <p className="text-xs text-navy/40 mt-1">{p.location}</p>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => openEdit(p)} data-testid="progress-edit" className="flex-1 inline-flex items-center justify-center gap-1 rounded-sm border border-navy/15 py-2 text-xs text-navy hover:bg-navy hover:text-white transition-colors">
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  <button onClick={() => remove(p.id)} data-testid="progress-delete" className="rounded-sm border border-navy/15 px-3 py-2 text-navy/50 hover:text-red-500 hover:border-red-300 transition-colors">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-navy">{editing ? "Edit project" : "New ongoing project"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <AdminField label="Title"><Input value={form.title} onChange={set("title")} data-testid="progress-title" className="rounded-sm" /></AdminField>
            <div className="grid grid-cols-2 gap-4">
              <AdminField label="Location"><Input value={form.location} onChange={set("location")} className="rounded-sm" /></AdminField>
              <AdminField label="Stage"><Input value={form.stage} onChange={set("stage")} placeholder="e.g. Slab casting" className="rounded-sm" /></AdminField>
            </div>
            <AdminField label="Order"><Input type="number" value={form.order} onChange={set("order")} className="rounded-sm" /></AdminField>
            <AdminField label="Stage-by-stage photos">
              <MultiImageUpload images={form.images || []} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} testid="progress-images" />
            </AdminField>
            <AdminField label="Description"><Textarea value={form.description} onChange={set("description")} rows={3} className="rounded-sm" /></AdminField>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-navy/15 px-5 py-2.5 text-sm text-navy">Cancel</button>
            <button onClick={save} data-testid="progress-save" className="rounded-sm bg-navy px-6 py-2.5 text-sm text-white hover:bg-gold hover:text-navy transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------------------------- Services */
const EMPTY_SERVICE = { title: "", description: "", icon: "building", order: 0 };

function Services() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SERVICE);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["services", "admin"],
    queryFn: async () => (await api.get("/services")).data,
  });

  const openNew = () => { setEditing(null); setForm(EMPTY_SERVICE); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY_SERVICE, ...p }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title) { toast.error("Title is required."); return; }
    const payload = { ...form, order: Number(form.order) || 0, details: (form.details || []).map((d) => d.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success(editing ? "Updated" : "Added");
      setOpen(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/services/${id}`);
    qc.invalidateQueries({ queryKey: ["services"] });
    toast.success("Deleted");
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-navy/50 text-sm">{items.length} services — shown on Home & the Services page</p>
        <button onClick={openNew} data-testid="service-add" className="inline-flex items-center gap-2 rounded-sm bg-navy px-5 py-2.5 text-white text-sm hover:bg-gold hover:text-navy transition-colors">
          <Plus className="h-4 w-4" /> Add service
        </button>
      </div>

      {isLoading ? <p className="text-navy/50">Loading…</p> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
          {items.map((p) => (
            <div key={p.id} className="rounded-sm border border-navy/10 bg-white p-6" data-testid="service-card">
              <span className="inline-block rounded-full bg-gold/15 px-3 py-0.5 text-xs uppercase tracking-wider text-gold">{p.icon}</span>
              <h3 className="mt-3 font-display text-lg text-navy">{p.title}</h3>
              <p className="mt-1 text-sm text-navy/55 leading-relaxed">{p.description}</p>
              <div className="mt-4 flex gap-2">
                <button onClick={() => openEdit(p)} data-testid="service-edit" className="flex-1 inline-flex items-center justify-center gap-1 rounded-sm border border-navy/15 py-2 text-xs text-navy hover:bg-navy hover:text-white transition-colors"><Pencil className="h-3 w-3" /> Edit</button>
                <button onClick={() => remove(p.id)} data-testid="service-delete" className="rounded-sm border border-navy/15 px-3 py-2 text-navy/50 hover:text-red-500 hover:border-red-300 transition-colors"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="service-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-navy">{editing ? "Edit service" : "New service"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <AdminField label="Title"><Input value={form.title} onChange={set("title")} data-testid="service-title" className="rounded-sm" /></AdminField>
            <div className="grid grid-cols-2 gap-4">
              <AdminField label="Icon">
                <select value={form.icon} onChange={set("icon")} data-testid="service-icon" className="h-10 w-full rounded-sm border border-input px-3 text-sm">
                  {SERVICE_ICON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </AdminField>
              <AdminField label="Order"><Input type="number" value={form.order} onChange={set("order")} className="rounded-sm" /></AdminField>
            </div>
            <AdminField label="Description"><Textarea value={form.description} onChange={set("description")} rows={2} className="rounded-sm" /></AdminField>
            <AdminField label="Sub-items / details (one per line — shown when a visitor clicks the service)">
              <Textarea
                value={(form.details || []).join("\n")}
                onChange={(e) => setForm((f) => ({ ...f, details: e.target.value.split("\n") }))}
                rows={5}
                data-testid="service-details"
                placeholder={"Modern house\nApartment\nDuplex"}
                className="rounded-sm text-sm"
              />
            </AdminField>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-navy/15 px-5 py-2.5 text-sm text-navy">Cancel</button>
            <button onClick={save} data-testid="service-save" className="rounded-sm bg-navy px-6 py-2.5 text-sm text-white hover:bg-gold hover:text-navy transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------------------------- Testimonials */
const EMPTY_TESTIMONIAL = { name: "", role: "", text: "", rating: 5, order: 0 };

function Testimonials() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TESTIMONIAL);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["testimonials", "admin"],
    queryFn: async () => (await api.get("/testimonials")).data,
  });

  const openNew = () => { setEditing(null); setForm(EMPTY_TESTIMONIAL); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY_TESTIMONIAL, ...p }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name || !form.text) { toast.error("Name and review text are required."); return; }
    const payload = { ...form, rating: Number(form.rating) || 5, order: Number(form.order) || 0 };
    try {
      if (editing) await api.put(`/testimonials/${editing.id}`, payload);
      else await api.post("/testimonials", payload);
      qc.invalidateQueries({ queryKey: ["testimonials"] });
      toast.success(editing ? "Updated" : "Added");
      setOpen(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/testimonials/${id}`);
    qc.invalidateQueries({ queryKey: ["testimonials"] });
    toast.success("Deleted");
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-navy/50 text-sm">{items.length} testimonials — shown on the homepage</p>
        <button onClick={openNew} data-testid="testimonial-add" className="inline-flex items-center gap-2 rounded-sm bg-navy px-5 py-2.5 text-white text-sm hover:bg-gold hover:text-navy transition-colors">
          <Plus className="h-4 w-4" /> Add testimonial
        </button>
      </div>

      {isLoading ? <p className="text-navy/50">Loading…</p> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="testimonials-grid">
          {items.map((p) => (
            <div key={p.id} className="rounded-sm border border-navy/10 bg-white p-6" data-testid="testimonial-card">
              <div className="flex gap-1 text-gold">{Array.from({ length: p.rating || 5 }).map((_, k) => <Star key={k} className="h-3.5 w-3.5 fill-gold" />)}</div>
              <p className="mt-3 text-sm text-navy/70 leading-relaxed">"{p.text}"</p>
              <p className="mt-3 text-xs uppercase tracking-wider text-navy/50">{p.name}{p.role ? ` · ${p.role}` : ""}</p>
              <div className="mt-4 flex gap-2">
                <button onClick={() => openEdit(p)} data-testid="testimonial-edit" className="flex-1 inline-flex items-center justify-center gap-1 rounded-sm border border-navy/15 py-2 text-xs text-navy hover:bg-navy hover:text-white transition-colors"><Pencil className="h-3 w-3" /> Edit</button>
                <button onClick={() => remove(p.id)} data-testid="testimonial-delete" className="rounded-sm border border-navy/15 px-3 py-2 text-navy/50 hover:text-red-500 hover:border-red-300 transition-colors"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="testimonial-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-navy">{editing ? "Edit testimonial" : "New testimonial"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <AdminField label="Client name"><Input value={form.name} onChange={set("name")} data-testid="testimonial-name" className="rounded-sm" /></AdminField>
              <AdminField label="Role / project"><Input value={form.role} onChange={set("role")} placeholder="e.g. Residential Villa" className="rounded-sm" /></AdminField>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <AdminField label="Rating (1-5)"><Input type="number" min="1" max="5" value={form.rating} onChange={set("rating")} className="rounded-sm" /></AdminField>
              <AdminField label="Order"><Input type="number" value={form.order} onChange={set("order")} className="rounded-sm" /></AdminField>
            </div>
            <AdminField label="Review text"><Textarea value={form.text} onChange={set("text")} data-testid="testimonial-text" rows={4} className="rounded-sm" /></AdminField>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-navy/15 px-5 py-2.5 text-sm text-navy">Cancel</button>
            <button onClick={save} data-testid="testimonial-save" className="rounded-sm bg-navy px-6 py-2.5 text-sm text-white hover:bg-gold hover:text-navy transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------------------------- Settings */
const FIELDS = [
  ["brand", "Brand name"], ["tagline", "Tagline"], ["phone_primary", "Primary phone"],
  ["phone_secondary", "Secondary phone"], ["email", "Email"], ["website", "Website"],
  ["address", "Address"], ["hours", "Working hours"], ["map_query", "Google Maps search query"],
  ["instagram", "Instagram URL"], ["facebook", "Facebook URL"], ["youtube", "YouTube URL"],
  ["rating", "Google rating"], ["years_experience", "Years of experience"],
  ["projects_completed", "Projects completed"],
  ["rate_classic", "Construction rate (₹/sq ft)"], ["rate_premium", "Construction + Interior rate (₹/sq ft)"],
  ["rate_luxury", "Only Interior rate (₹/sq ft)"], ["expert_team", "Expert team size"],
];

function SiteSettings() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { isLoading } = useQuery({
    queryKey: ["settings", "admin"],
    queryFn: async () => {
      const data = (await api.get("/settings")).data;
      setForm(data);
      return data;
    },
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !form) return <p className="text-navy/50">Loading…</p>;

  return (
    <div className="max-w-3xl" data-testid="settings-form">
      <div className="rounded-sm border border-navy/10 bg-white p-6 lg:p-8">
        <div className="grid gap-5 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <AdminField key={k} label={label} full={k === "address"}>
              <Input value={form[k] || ""} onChange={set(k)} data-testid={`settings-${k}`} className="rounded-sm" />
            </AdminField>
          ))}
          <AdminField label="About / company description" full>
            <Textarea value={form.about || ""} onChange={set("about")} rows={5} data-testid="settings-about" className="rounded-sm" />
          </AdminField>
        </div>

        <div className="mt-8 border-t border-navy/10 pt-6">
          <h3 className="font-display text-xl text-navy">Homepage hero photos</h3>
          <p className="text-sm text-navy/50 mt-1 mb-4">Upload your own Lucknow site / project photos. These replace the default hero slideshow. Leave empty to use defaults.</p>
          <MultiImageUpload
            images={form.hero_images || []}
            onChange={(imgs) => setForm((f) => ({ ...f, hero_images: imgs }))}
            testid="settings-hero-images"
          />
        </div>

        <div className="mt-8 border-t border-navy/10 pt-6">
          <h3 className="font-display text-xl text-navy">Company brochure (PDF)</h3>
          <p className="text-sm text-navy/50 mt-1 mb-4">Upload your brochure PDF — the Brochure page download button will serve this file.</p>
          <div className="grid gap-4 sm:grid-cols-2 mb-4">
            <AdminField label="Brochure title"><Input value={form.brochure_title || ""} onChange={set("brochure_title")} data-testid="settings-brochure-title" className="rounded-sm" /></AdminField>
            <AdminField label="Brochure description"><Input value={form.brochure_description || ""} onChange={set("brochure_description")} data-testid="settings-brochure-desc" className="rounded-sm" /></AdminField>
          </div>
          <FileUpload
            value={form.brochure_url || ""}
            onChange={(url) => setForm((f) => ({ ...f, brochure_url: url }))}
            accept=".pdf"
            label="Brochure PDF"
            testid="settings-brochure-upload"
          />
        </div>

        <button onClick={save} disabled={saving} data-testid="settings-save" className="mt-8 rounded-sm bg-navy px-8 py-3 text-white hover:bg-gold hover:text-navy transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------- Shared */
function StatCard({ label, value }) {
  return (
    <div className="rounded-sm border border-navy/10 bg-white p-6">
      <div className="font-display text-4xl text-navy">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-[0.2em] text-navy/40">{label}</div>
    </div>
  );
}

function EmptyState({ title, desc }) {
  return (
    <div className="rounded-sm border border-dashed border-navy/20 bg-white p-16 text-center">
      <Building2 className="mx-auto h-10 w-10 text-navy/20" />
      <h3 className="mt-4 font-display text-xl text-navy">{title}</h3>
      <p className="mt-1 text-sm text-navy/50">{desc}</p>
    </div>
  );
}

function AdminField({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="mb-1.5 block text-xs uppercase tracking-[0.12em] text-navy/45">{label}</span>
      {children}
    </label>
  );
}
